import React from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  ProductTelemetryController,
  ProductTelemetryEvent,
} from '../product-telemetry/product-telemetry-controller';

import { SmartlookEventInformation, trackWithSmartlook } from '../util/track-with-smartlook';

interface Props {
  size?: 'default' | 'xxs' | 'xs' | 'sm' | 'lg';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'dismiss' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  selected?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  linkTo?: string;
  type?: React.ButtonHTMLAttributes<any>['type'];
  form?: React.ButtonHTMLAttributes<any>['form'];
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
  startIcon?: React.ReactNode;
  /**
   * Use for creating a custom event in Smartlook to track usage of the onClick event.
   * @see {@link https://teamengine.atlassian.net/wiki/spaces/PD/pages/2700804314/Using+Custom+Smartlook+Events Using Custom Smartlook Events}
   * @example
   * eventName: 'User Identification Response',
   * props: { action: 'submit' | 'skip', ... },
   * @example
   * eventName: 'Helpscout Beacon Click',
   * props: { interaction: 'open' | 'close', ... },
   * @example
   * eventName: 'Clicked Send Message',
   * props: { messageType: 'email' | 'text', ... },
   */
  smartlookEvent?: SmartlookEventInformation;

  /**
   * Cypress selector for identifying buttons in end-to-end testing
   * sets the data-cy attribute on the button element
   * @see https://docs.cypress.io/guides/references/best-practices#Selecting-Elements
   */
  dataCy?: string;

  productTelemetryEvent?: ProductTelemetryEvent;

  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  style?: any;
}

export const UiButton = (props: React.PropsWithChildren<Props>) => {
  const {
    loading,
    linkTo,
    smartlookEvent,
    onClick,
    dataCy,
    productTelemetryEvent,
    startIcon,
    color,
    ...passAlongProps
  } = props;
  let muiVariant: 'contained' | 'outlined' | 'text' | undefined;
  let spinnerSize: number;
  const {
    children,
    size = 'default',
    variant = 'primary',
    className,
    selected,
    disabled,
    target,
    rel,
  } = passAlongProps;

  const muiColor =
    typeof color !== 'undefined' ? color : variant === 'primary' ? 'secondary' : 'default';
  const classes = classNames(
    `button--${size}`,
    className,
    // We use the dismiss style button for some smaller buttons, and we want those to have the secondary color
    // by default, and also change colors like regular buttons when loading. So, if the color is passed in above
    // we turn off this CSS rule which stomps on color
    { 'button-dismiss': variant === 'dismiss' && !color },
    { 'button--selected': selected },
  );

  switch (variant) {
    case 'primary':
    case 'secondary':
      muiVariant = 'contained';
      break;
    case 'tertiary':
      muiVariant = 'outlined';
      break;
    default:
      muiVariant = undefined;
      break;
  }

  switch (size) {
    case 'default':
      spinnerSize = 20;
      break;
    case 'xxs':
      spinnerSize = 10;
      break;
    case 'xs':
      spinnerSize = 10;
      break;
    case 'sm':
      spinnerSize = 15;
      break;
    case 'lg':
      spinnerSize = 25;
      break;
    default:
      spinnerSize = 10;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (smartlookEvent) {
      trackWithSmartlook(smartlookEvent);
    }
    if (productTelemetryEvent) {
      ProductTelemetryController.logEvent(productTelemetryEvent.event, {
        value: productTelemetryEvent.value,
        metadata: productTelemetryEvent.metadata,
      });
    }
    if (onClick) {
      onClick(event);
    }
  };

  const button = (
    <Button
      {...passAlongProps}
      onClick={handleClick}
      size="medium"
      variant={muiVariant}
      color={muiColor}
      className={classes}
      disabled={disabled || loading}
      data-cy={dataCy}
      startIcon={startIcon}
      classes={startIcon ? { startIcon: 'u-margin-right-xs' } : undefined}
    >
      {loading && (
        <CircularProgress color="inherit" className="u-margin-right" size={spinnerSize} />
      )}
      {children}
    </Button>
  );

  return linkTo ? (
    <Link to={linkTo} rel={rel} target={target} data-cy={dataCy}>
      {button}
    </Link>
  ) : (
    button
  );
};
